// Generated from scripts/predictive-search.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';
/** Every row the arrow keys walk, in document order: suggestions, products, collections, content, or the recent searches. */
const OPTION_SELECTOR = '[role="option"]';
/** The Predictive Search API resource types the panel lists. Groups render in this order. */
const RESOURCE_TYPES = 'product,collection,page,article,query';
/**
 * With `resources[limit_scope]=each`, `resources[limit]` is one number for every type, so the request asks for the
 * largest per-type limit (products). The section trims collections, pages, articles and suggestions to their own.
 */
const RESOURCE_LIMIT = 6;
/** Responses kept in memory, newest last, keyed by the full request URL. A term without results is cached like any other. */
const CACHE_SIZE = 5;
/** Recent searches live in this browser only and hold the typed text and nothing else. */
const RECENT_KEY = 'wontio:recent-searches';
const RECENT_LIMIT = 5;
/** The `type` the results page accepts, and the one a "view all" opens when no row says otherwise. */
const SEARCH_TYPES = ['product', 'page', 'article'];
const DEFAULT_SEARCH_TYPE = 'product';
function isSearchType(value) {
    return value !== undefined && SEARCH_TYPES.includes(value);
}
function readRecent() {
    try {
        const stored = JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? '[]');
        if (!Array.isArray(stored))
            return [];
        return stored.filter((query) => typeof query === 'string' && query.trim() !== '').slice(0, RECENT_LIMIT);
    }
    catch {
        return [];
    }
}
function writeRecent(queries) {
    try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(queries.slice(0, RECENT_LIMIT)));
    }
    catch {
        // Storage can be unavailable (private mode, blocked site data); recent searches then last for the page only.
    }
}
/** Parses a section response and returns the section's wrapper, or throws when it holds no search results. */
function parseResponse(markup) {
    const wrapper = new DOMParser().parseFromString(markup, 'text/html').body.firstElementChild;
    if (!wrapper?.querySelector('[data-search-state]'))
        throw new Error('The search response held no results markup.');
    return wrapper;
}
/** Typing suggestion pacing in milliseconds: per character typed, per character deleted, a whole phrase held, the empty field between phrases. */
const TYPE_DELAY = 55;
const DELETE_DELAY = 28;
const HOLD_DELAY = 1800;
const GAP_DELAY = 350;
/** Custom properties the open dialog carries: the visible viewport's height, and its offset from the layout viewport's top. */
const VIEWPORT_HEIGHT = '--search-viewport-height';
const VIEWPORT_OFFSET = '--search-viewport-offset';
/**
 * Types the merchant's suggested phrases into the empty search field one character at a time,
 * holds each, deletes it and moves to the next. The host carries `data-typing` ("typing" while
 * characters move, "idle" while a phrase holds) so the stylesheet shows the overlay, hides the
 * field's own caret and blinks the suggestion caret. Under reduced motion nothing types: the first
 * phrase becomes the field's still placeholder.
 */
class TypingPlaceholder {
    #host;
    #input;
    #text;
    #phrases;
    #placeholder;
    #timer;
    #phrase = 0;
    #length = 0;
    #deleting = false;
    constructor(host, input, text, phrases) {
        this.#host = host;
        this.#input = input;
        this.#text = text;
        this.#phrases = phrases;
        this.#placeholder = input.placeholder;
    }
    get running() {
        return this.#timer !== undefined;
    }
    /** Starts typing from the current phrase; a no-op while running, without phrases or under reduced motion. */
    start() {
        const first = this.#phrases[0];
        if (!first || this.running)
            return;
        if (prefersReducedMotion()) {
            this.#input.placeholder = first;
            return;
        }
        this.#input.placeholder = '';
        this.#host.dataset.typing = 'idle';
        this.#schedule(GAP_DELAY);
    }
    /** Stops typing and gives the field its own placeholder and caret back. */
    stop() {
        window.clearTimeout(this.#timer);
        this.#timer = undefined;
        this.#length = 0;
        this.#deleting = false;
        this.#text.textContent = '';
        delete this.#host.dataset.typing;
        this.#input.placeholder = this.#placeholder;
    }
    #schedule(delay) {
        this.#timer = window.setTimeout(this.#step, delay);
    }
    #step = () => {
        this.#timer = undefined;
        if (prefersReducedMotion()) {
            this.stop();
            this.start();
            return;
        }
        const phrase = this.#phrases[this.#phrase] ?? '';
        const characters = [...phrase];
        this.#length += this.#deleting ? -1 : 1;
        this.#text.textContent = characters.slice(0, this.#length).join('');
        if (!this.#deleting && this.#length >= characters.length) {
            this.#deleting = true;
            this.#host.dataset.typing = 'idle';
            this.#schedule(HOLD_DELAY);
            return;
        }
        if (this.#deleting && this.#length <= 0) {
            this.#deleting = false;
            this.#phrase = (this.#phrase + 1) % this.#phrases.length;
            this.#host.dataset.typing = 'idle';
            this.#schedule(GAP_DELAY);
            return;
        }
        this.#host.dataset.typing = 'typing';
        this.#schedule(this.#deleting ? DELETE_DELAY : TYPE_DELAY);
    };
}
/**
 * Live search inside the search dialog: fetches the predictive search section as the shopper types and
 * lists suggestions, products, collections and content as one combobox list. Focus stays in the field;
 * the arrow keys, Home and End move `aria-activedescendant` along every option, Enter follows the active
 * one, and Tab leaves the list for the next control. The field clearing or the dialog closing returns the
 * server-rendered empty state, which lists the recent searches kept in this browser. While the field is
 * empty it types the merchant's suggested phrases from `data-typing-phrases`, one per line.
 *
 * Requests wait 200 ms after the last keystroke, none leaves for an empty field, a newer one aborts the
 * older, and the last five responses are served from memory. A failed request leaves the last good list
 * in place and shows a quiet line.
 *
 * The dialog opens in the task of the shopper's tap, so the field is focused there and the phone
 * keyboard rises with it; focus must never move to a frame, timer or promise after the open. While
 * the dialog is open the visual viewport is published on the dialog so the panel stays above the keyboard.
 * Escape closes the dialog through its owner, which also resets the field and the list.
 */
class PredictiveSearchComponent extends Component {
    #lifetime;
    #request;
    #viewport;
    #emptyState;
    #keyboardNavigation = false;
    #typing;
    #open = false;
    #cache = new Map();
    get #input() {
        const { searchInput } = this.refs;
        if (!(searchInput instanceof HTMLInputElement))
            throw new Error('predictive-search-component needs ref="searchInput".');
        return searchInput;
    }
    get #form() {
        const { form } = this.refs;
        if (!(form instanceof HTMLFormElement))
            throw new Error('predictive-search-component needs ref="form" on its form.');
        return form;
    }
    /** The hidden `type` field of the form: the kind of result the results page opens on. */
    get #typeInput() {
        const { searchType } = this.refs;
        return searchType instanceof HTMLInputElement ? searchType : undefined;
    }
    get #results() {
        const { predictiveSearchResults } = this.refs;
        if (!(predictiveSearchResults instanceof HTMLElement))
            throw new Error('predictive-search-component needs ref="predictiveSearchResults".');
        return predictiveSearchResults;
    }
    get #resetButton() {
        const { resetButton } = this.refs;
        return resetButton instanceof HTMLElement ? resetButton : undefined;
    }
    /** The polite live region; it lives outside the results, so a morph never replaces it. */
    get #status() {
        const { status } = this.refs;
        return status instanceof HTMLElement ? status : undefined;
    }
    get #errorLine() {
        const { errorLine } = this.refs;
        return errorLine instanceof HTMLElement ? errorLine : undefined;
    }
    get #dialog() {
        return this.closest('dialog-component');
    }
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = new AbortController();
        const { signal } = this.#lifetime;
        // The server renders the empty state into the results container; keep it to restore without a request.
        if (!this.#emptyState) {
            const emptyState = this.#results.cloneNode(true);
            if (emptyState instanceof Element)
                this.#emptyState = emptyState;
        }
        if (this.#resetButton)
            this.#resetButton.hidden = this.#input.value.length === 0;
        // Focus leaving the field (Tab) leaves no option looking active.
        this.#input.addEventListener('blur', this.#onBlur, { signal });
        this.#renderRecent();
        const { typingText } = this.refs;
        const phrases = (this.dataset.typingPhrases ?? '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
        if (typingText instanceof HTMLElement && phrases.length) {
            this.#typing = new TypingPlaceholder(this, this.#input, typingText, phrases);
            this.#input.addEventListener('input', () => this.#syncTyping(), { signal });
            document.addEventListener('visibilitychange', () => this.#syncTyping(), { signal });
        }
        const dialog = this.#dialog;
        this.#open = this.closest('dialog')?.open ?? true;
        if (dialog) {
            document.addEventListener('keydown', this.#onShortcut, { signal });
            dialog.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen, { signal });
            dialog.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose, { signal });
            // This module can load after the shopper has opened the dialog; the open event has then passed.
            if (this.closest('dialog')?.open)
                this.#trackViewport();
        }
        this.#syncTyping();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        this.#request?.abort();
        this.search.cancel();
        this.#typing?.stop();
        this.#stopViewport();
    }
    /** Runs synchronously inside the opening gesture: the focus here is what raises the keyboard on iOS. */
    #onDialogOpen = () => {
        this.#open = true;
        this.#input.focus({ preventScroll: true });
        this.#trackViewport();
        this.#renderRecent();
        this.#syncTyping();
    };
    #onDialogClose = () => {
        this.#open = false;
        this.#stopViewport();
        this.#reset();
    };
    /**
     * Publishes the visible viewport on the dialog while it is open, at most once per frame: its
     * height shrinks when the on-screen keyboard rises, and its offset follows iOS panning the page
     * under the keyboard. The stylesheet falls back to `100dvh` without the height.
     */
    #trackViewport() {
        const dialog = this.closest('dialog');
        const viewport = window.visualViewport;
        if (!dialog || !viewport)
            return;
        this.#viewport?.abort();
        const tracking = new AbortController();
        this.#viewport = tracking;
        const { signal } = tracking;
        let frame = 0;
        const write = () => {
            frame = 0;
            dialog.style.setProperty(VIEWPORT_HEIGHT, `${viewport.height}px`);
            dialog.style.setProperty(VIEWPORT_OFFSET, `${viewport.offsetTop}px`);
        };
        const schedule = () => {
            frame ||= requestAnimationFrame(write);
        };
        signal.addEventListener('abort', () => {
            cancelAnimationFrame(frame);
            dialog.style.removeProperty(VIEWPORT_HEIGHT);
            dialog.style.removeProperty(VIEWPORT_OFFSET);
        });
        write();
        viewport.addEventListener('resize', schedule, { signal });
        viewport.addEventListener('scroll', schedule, { signal });
    }
    #stopViewport() {
        this.#viewport?.abort();
        this.#viewport = undefined;
    }
    /** The suggestion types only while the dialog is open, the page is visible and the field is empty. */
    #syncTyping() {
        const typing = this.#typing;
        if (!typing)
            return;
        if (this.#open && !document.hidden && !this.#input.value)
            typing.start();
        else
            typing.stop();
    }
    /** Searches for the field's value; non-typing input is ignored. An empty field requests nothing. */
    search = debounce((event) => {
        if (!event.inputType)
            return;
        const term = this.#input.value.trim();
        this.#select(-1);
        if (!term) {
            this.#reset();
            return;
        }
        if (this.#resetButton)
            this.#resetButton.hidden = false;
        void this.#fetchResults(term);
    }, 200);
    /** Clears the field and its results, keeping focus in the field. */
    resetSearch = () => {
        this.#input.focus();
        this.#reset();
    };
    /** Forgets every recent search and returns focus to the field, which the hidden clear button had. */
    clearRecent = () => {
        writeRecent([]);
        this.#renderRecent();
        this.#input.focus();
    };
    /**
     * A click on a row of the list remembers what the shopper searched; a click on anything but a control
     * returns focus to the field, so typing continues.
     */
    handleModalClick(event) {
        // The delegated handler receives the element that carries the `on:click` attribute as `target`; the click itself is first in the path.
        const origin = event.composedPath()[0];
        const target = origin instanceof Element ? origin : undefined;
        const row = target?.closest(OPTION_SELECTOR);
        if (row) {
            this.#followed(row);
            return;
        }
        if (target?.closest('a, button, input'))
            return;
        this.#input.focus();
    }
    /** The form submits through the browser, so both the keyboard's search key and the view-all button land here. */
    onSubmit() {
        this.#remember(this.#input.value);
    }
    /**
     * Arrow keys walk every option as one list, wrapping at the ends. Home and End jump to the first and
     * last option once one is active; before that they move the caret, as in any text field. Enter follows
     * the active option, a lone result, or submits the search. Tab and Escape are left to the browser and
     * the dialog owner: Tab moves to the next control, Escape closes the dialog.
     */
    onSearchKeyDown(event) {
        if (event.isComposing)
            return;
        const options = this.#options();
        const current = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp': {
                if (!options.length)
                    return;
                event.preventDefault();
                this.#keyboardNavigation = true;
                const forward = event.key === 'ArrowDown';
                const next = forward ? (current < options.length - 1 ? current + 1 : 0) : current > 0 ? current - 1 : options.length - 1;
                this.#select(next, options);
                break;
            }
            case 'Home':
            case 'End': {
                if (current === -1)
                    return;
                event.preventDefault();
                this.#keyboardNavigation = true;
                this.#select(event.key === 'Home' ? 0 : options.length - 1, options);
                break;
            }
            case 'Enter': {
                event.preventDefault();
                this.#enter(options[current]);
                break;
            }
        }
    }
    /**
     * Follows the active option; without one, a lone result opens directly and anything else is a search.
     * Clicking the option's link takes the same path as a pointer click, which remembers the search.
     */
    #enter(active) {
        if (active instanceof HTMLAnchorElement) {
            active.click();
            return;
        }
        const single = this.#results.querySelector('[data-single-result-url]')?.dataset.singleResultUrl;
        if (single) {
            this.#remember(this.#input.value);
            window.location.href = single;
            return;
        }
        this.#form.requestSubmit();
    }
    /** Cmd+K and Ctrl+K toggle the dialog; preventing the default keeps the browser's own Ctrl+K from running. */
    #onShortcut = (event) => {
        if (event.defaultPrevented || event.isComposing || event.altKey || event.shiftKey)
            return;
        if (!(event.metaKey || event.ctrlKey) || (event.key !== 'k' && event.key !== 'K'))
            return;
        event.preventDefault();
        this.#dialog?.toggleDialog();
    };
    #options() {
        return [...this.#results.querySelectorAll(OPTION_SELECTOR)];
    }
    #onBlur = () => {
        this.#select(-1, undefined, false);
    };
    /**
     * Marks one option as the active one: `aria-selected` on the row, `aria-activedescendant` on the field,
     * and the hidden `type` field set to the kind of result the row is. -1 clears the selection and leaves
     * the type as it was, so tabbing from a row to the view-all button keeps the kind the shopper was on.
     * Focus stays in the field.
     */
    #select(index, options = this.#options(), refocus = true) {
        options.forEach((option, position) => {
            const selected = position === index;
            option.classList.toggle('keyboard-focus', selected && this.#keyboardNavigation);
            if (selected)
                option.setAttribute('aria-selected', 'true');
            else
                option.removeAttribute('aria-selected');
        });
        const active = options[index];
        if (!active) {
            this.#input.removeAttribute('aria-activedescendant');
            return;
        }
        this.#input.setAttribute('aria-activedescendant', active.id);
        this.#syncType(active.dataset.resultType);
        active.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
        if (refocus)
            this.#input.focus({ preventScroll: true });
    }
    /** Points the hidden `type` field at a kind of result; anything but products, pages and articles means the response's default. */
    #syncType(resultType) {
        const input = this.#typeInput;
        if (!input)
            return;
        const fallback = this.#results.querySelector('[data-default-type]')?.dataset.defaultType;
        input.value = isSearchType(resultType) ? resultType : isSearchType(fallback) ? fallback : DEFAULT_SEARCH_TYPE;
    }
    /** Where "view all" and a recent search open: the form's own fields with the given words, as the browser would submit them. */
    #searchUrl(query) {
        const url = new URL(this.#form.action, location.origin);
        url.search = '';
        for (const [name, value] of new FormData(this.#form)) {
            if (typeof value === 'string')
                url.searchParams.set(name, value);
        }
        url.searchParams.set('q', query);
        return url;
    }
    #requestUrl(term, sectionId) {
        const url = new URL(Theme.routes.predictive_search_url, location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', RESOURCE_TYPES);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[limit]', String(RESOURCE_LIMIT));
        url.searchParams.set('section_id', sectionId);
        return url;
    }
    async #fetchResults(term) {
        const sectionId = this.dataset.sectionId;
        if (!sectionId)
            return;
        this.#request?.abort();
        const request = new AbortController();
        this.#request = request;
        const url = this.#requestUrl(term, sectionId);
        const deferred = SearchUpdateEvent.createPromise();
        // `detail.surface` tells the analytics bridge which interface asked: the instant results, not a results page.
        this.dispatchEvent(new SearchUpdateEvent({ search: { query: term }, promise: deferred.promise, detail: { surface: 'predictive' } }));
        try {
            const markup = this.#cache.get(url.href) ?? (await this.#load(url, request.signal));
            // A newer search or a reset replaced this one while it was in flight: its answer must not paint.
            if (this.#request !== request) {
                deferred.reject(new DOMException('Search superseded', 'AbortError'));
                return;
            }
            const response = parseResponse(markup);
            this.#store(url.href, markup);
            this.#render(response);
            deferred.resolve({ totalCount: this.#options().filter((option) => option.dataset.resultType !== 'query').length });
        }
        catch (error) {
            deferred.reject(error);
            if (this.#request === request && !request.signal.aborted)
                this.#showError();
        }
    }
    async #load(url, signal) {
        const response = await fetch(url, { signal });
        if (!response.ok)
            throw new Error(`The search request failed with status ${response.status}.`);
        return response.text();
    }
    /** Keeps the newest responses, most recently used last; the oldest leaves when the cache is full. */
    #store(key, markup) {
        this.#cache.delete(key);
        this.#cache.set(key, markup);
        for (const oldest of this.#cache.keys()) {
            if (this.#cache.size <= CACHE_SIZE)
                break;
            this.#cache.delete(oldest);
        }
    }
    /** Morphs a response into the list, then publishes what it holds: expanded state, default type, the count. */
    #render(response) {
        morph(this.#results, response);
        const root = this.#results.querySelector('[data-search-state]');
        this.#select(-1);
        this.#syncType();
        this.#clearError();
        this.dataset.state = root?.dataset.searchState === 'empty' ? 'empty' : 'results';
        this.#input.setAttribute('aria-expanded', String(this.#options().length > 0));
        this.#announce(root?.dataset.announcement ?? '');
        this.#scrollToTop();
    }
    #reset() {
        this.#request?.abort();
        this.#request = undefined;
        this.search.cancel();
        this.#select(-1);
        this.#input.value = '';
        if (this.#resetButton)
            this.#resetButton.hidden = true;
        const emptyState = this.#emptyState?.cloneNode(true);
        if (emptyState instanceof Element)
            morph(this.#results, emptyState);
        this.#clearError();
        this.#announce('');
        this.#renderRecent();
        this.#syncType();
        this.#scrollToTop();
        this.#syncTyping();
    }
    /** One polite message per response, written to a region that outlives every morph. */
    #announce(message) {
        const status = this.#status;
        if (status)
            status.textContent = message;
    }
    /** A failed request keeps the last good list and says so in a quiet line. */
    #showError() {
        const line = this.#errorLine;
        if (!line)
            return;
        line.hidden = false;
        this.#announce(line.textContent?.trim() ?? '');
    }
    #clearError() {
        const line = this.#errorLine;
        if (line)
            line.hidden = true;
    }
    /** Remembers a row's words: a recent search's own, or the text the shopper typed to reach the row. */
    #followed(row) {
        this.#remember(row.dataset.recentQuery ?? this.#input.value);
    }
    /** Stores a search as the newest of five unique recent searches. Only the trimmed text is kept. */
    #remember(query) {
        const words = query.trim();
        if (!words)
            return;
        const rest = readRecent().filter((recent) => recent.toLowerCase() !== words.toLowerCase());
        writeRecent([words, ...rest]);
    }
    /** Fills the empty state's recent searches from storage and hides the group when there are none. */
    #renderRecent() {
        const group = this.#results.querySelector('[data-recent]');
        const list = group?.querySelector('[data-recent-list]');
        const template = group?.querySelector('template');
        if (!group || !list || !(template instanceof HTMLTemplateElement))
            return;
        const queries = readRecent();
        list.replaceChildren(...queries.flatMap((query, index) => this.#recentOption(template, query, index)));
        group.hidden = queries.length === 0;
        this.dataset.state = queries.length > 0 ? 'recent' : 'idle';
        this.#input.setAttribute('aria-expanded', String(queries.length > 0));
    }
    #recentOption(template, query, index) {
        const row = template.content.cloneNode(true);
        if (!(row instanceof DocumentFragment))
            return [];
        // The option is the link itself; the list item around it is presentational and is what the list holds.
        const option = row.querySelector(OPTION_SELECTOR);
        const item = row.firstElementChild;
        const text = row.querySelector('[data-recent-text]');
        if (!(option instanceof HTMLAnchorElement) || !(item instanceof HTMLElement) || !text)
            return [];
        option.id = `predictive-search-option-recent-${index + 1}`;
        option.dataset.recentQuery = query;
        option.href = this.#searchUrl(query).href;
        text.textContent = query;
        return [item];
    }
    #scrollToTop() {
        requestAnimationFrame(() => this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0));
    }
}
if (!customElements.get('predictive-search-component')) {
    customElements.define('predictive-search-component', PredictiveSearchComponent);
}
